import PermisosTableros from "../Tableros/PermisosTableros";
// src/components/UsuariosTemporales/CrearUsuarioTemporal.jsx
import React, { useEffect, useState } from "react";
import DuracionTemporal from "./DuracionTemporal";
import { duracionEnHoras } from "./duracionTemporalUtils";
import api from "../../services/Api";
import { limitarPermisosAlCreador, permissionTemplate } from "../../constants/permissionTemplates";
import { usePermisos } from "../../context/PermisosContext";
import { isSuperAdmin } from "../../services/authUtils";
import { FEATURE_BITS as FB, BITSET_LEN, bitsetPredeterminadoPorRol } from "../../constants/permisos";
import { FIXED_PERMISSION_BITS, forceFixedPermissions } from "../../constants/permisos";
import usuarioService from "../../services/usuarioService";

/**
 * Ventanas y sus features (VIEW/EDIT) para el chip principal.
 * Incluye Alarmas con permisos separados para crear, administrar e historial.
 */
const WINDOW_TO_FEATURES = {
  Inicio: ["INICIO_VIEW", "INICIO_EDIT"],
  Estanques: ["ESTANQUES_VIEW", "ESTANQUES_EDIT"],
  Dispositivos: ["DISPOSITIVOS_VIEW", "DISPOSITIVOS_EDIT"],
  Sensores: ["SENSORES_VIEW", "SENSORES_EDIT"],
  Efectores: ["EFECTORES_VIEW", "EFECTORES_EDIT"],
  Automatización: ["AUTOMATIZACION_VIEW", "AUTOMATIZACION_EDIT"],
  Alarmas: ["ALARMAS_VIEW", "ALARMAS_EDIT"],
  "Usuarios Temporales": ["USUARIOS_TEMP_VIEW", "USUARIOS_TEMP_EDIT"],
  Reportería: ["REPORTERIA_VIEW", "REPORTERIA_EDIT"],
  Configuraciones: ["CONFIGURACIONES_VIEW", "CONFIGURACIONES_EDIT"],
  "Acerca de": ["ACERCA_VIEW"],
  "Contáctanos": ["CONTACTO_VIEW"],
};

const WINDOWS_ORDER = [
  "Inicio",
  "Estanques",
  "Dispositivos",
  "Sensores",
  "Efectores",
  "Automatización",
  "Alarmas",
  "Usuarios Temporales",
  "Reportería",
  "Configuraciones",
  "Acerca de",
  "Contáctanos",
];
const UNIDAD_EXPIRACION_LABEL = { horas: "horas", dias: "días", meses: "meses", anios: "años" };

// === Helpers bitset ===
const rawEmptyBitset = () => "0".repeat(BITSET_LEN);

// setBit con protección: el bit 1 (posición 1) nunca puede quedar en 0
const setBit = (bitset, oneBasedPos, value) => {
  const idx = oneBasedPos - 1;
  if (idx < 0 || idx >= BITSET_LEN) return bitset;
  if (FIXED_PERMISSION_BITS.includes(oneBasedPos) && !value) value = true; // fuerza INICIO_VIEW
  return bitset.substring(0, idx) + (value ? "1" : "0") + bitset.substring(idx + 1);
};

const getBit = (bitset, oneBasedPos) => {
  const idx = oneBasedPos - 1;
  if (idx < 0 || idx >= bitset.length) return false;
  return bitset[idx] === "1";
};

// fuerza el bit 1 a "1"
const forceBit1 = forceFixedPermissions;
const emptyBitset = () => forceBit1(rawEmptyBitset());
const clampToLength = (s, len = BITSET_LEN) =>
  forceBit1((s || "").padEnd(len, "0").slice(0, len));

const isWindowActive = (w, bs) =>
  (WINDOW_TO_FEATURES[w] || []).some((f) => getBit(bs, FB[f]));

/**
 * Pestañas por ventana y sus bits (VIEW/EDIT) según constants/permisos.js
 */
const TABS_BITS = {
  // Inicio (nuevas pestañas)
  Inicio: [
    { key: "resumen", label: "Resumen", viewBit: FB.INICIO_TAB_RESUMEN_VIEW, editBit: FB.INICIO_TAB_RESUMEN_EDIT },
    { key: "bienvenida", label: "Bienvenida", viewBit: FB.INICIO_TAB_BIENVENIDA_VIEW, editBit: FB.INICIO_TAB_BIENVENIDA_EDIT },
    { key: "misSensores", label: "Mis Sensores", viewBit: FB.INICIO_TAB_MIS_SENSORES_VIEW, editBit: FB.INICIO_TAB_MIS_SENSORES_EDIT },
    { key: "misEstadisticas", label: "Mis Estadísticas", viewBit: FB.INICIO_TAB_MIS_ESTADISTICAS_VIEW, editBit: FB.INICIO_TAB_MIS_ESTADISTICAS_EDIT },
  ],

  Estanques: [
    { key: "tablero", label: "Tablero", viewBit: FB.ESTANQUES_TAB_TABLERO_VIEW, editBit: FB.ESTANQUES_TAB_TABLERO_EDIT },
    { key: "crear", label: "Crear", viewBit: FB.ESTANQUES_TAB_CREAR_VIEW, editBit: FB.ESTANQUES_TAB_CREAR_EDIT },
    { key: "editar", label: "Editar", viewBit: FB.ESTANQUES_TAB_EDITAR_VIEW, editBit: FB.ESTANQUES_TAB_EDITAR_EDIT },
  ],
  Dispositivos: [
    { key: "asistente", label: "Asistente", viewBit: FB.DISPOSITIVOS_TAB_ASISTENTE_VIEW, editBit: FB.DISPOSITIVOS_TAB_ASISTENTE_EDIT },
    { key: "tablero", label: "Tablero", viewBit: FB.DISPOSITIVOS_TAB_TABLERO_VIEW, editBit: FB.DISPOSITIVOS_TAB_TABLERO_EDIT },
    { key: "crear", label: "Crear", viewBit: FB.DISPOSITIVOS_TAB_CREAR_VIEW, editBit: FB.DISPOSITIVOS_TAB_CREAR_EDIT },
    { key: "editar", label: "Editar", viewBit: FB.DISPOSITIVOS_TAB_EDITAR_VIEW, editBit: FB.DISPOSITIVOS_TAB_EDITAR_EDIT },
  ],
  Sensores: [
    { key: "tablero", label: "Tablero", viewBit: FB.SENSORES_TAB_TABLERO_VIEW, editBit: FB.SENSORES_TAB_TABLERO_EDIT },
    { key: "crear", label: "Crear", viewBit: FB.SENSORES_TAB_CREAR_VIEW, editBit: FB.SENSORES_TAB_CREAR_EDIT },
    { key: "editar", label: "Editar", viewBit: FB.SENSORES_TAB_EDITAR_VIEW, editBit: FB.SENSORES_TAB_EDITAR_EDIT },
  ],
  Efectores: [
    { key: "tablero", label: "Tablero", viewBit: FB.EFECTORES_TAB_TABLERO_VIEW, editBit: FB.EFECTORES_TAB_TABLERO_EDIT },
    { key: "crear", label: "Crear", viewBit: FB.EFECTORES_TAB_CREAR_VIEW, editBit: FB.EFECTORES_TAB_CREAR_EDIT },
    { key: "editar", label: "Editar", viewBit: FB.EFECTORES_TAB_EDITAR_VIEW, editBit: FB.EFECTORES_TAB_EDITAR_EDIT },
  ],
  Automatización: [
    { key: "tablero", label: "Tablero", viewBit: FB.AUTOMATIZACION_TAB_TABLERO_VIEW, editBit: FB.AUTOMATIZACION_TAB_TABLERO_EDIT },
    { key: "porSensores", label: "Por Sensores", viewBit: FB.AUTOMATIZACION_TAB_SENSORES_VIEW, editBit: FB.AUTOMATIZACION_TAB_SENSORES_EDIT },
    { key: "porEfectores", label: "Por Efectores", viewBit: FB.AUTOMATIZACION_TAB_EFECTORES_VIEW, editBit: FB.AUTOMATIZACION_TAB_EFECTORES_EDIT },
  ],
  Alarmas: [
    { key: "tablero", label: "Tablero", viewBit: FB.ALARMAS_TAB_TABLERO_VIEW, editBit: FB.ALARMAS_TAB_TABLERO_EDIT },
    { key: "crearAlarma", label: "Crear", viewBit: FB.ALARMAS_TAB_CREAR_VIEW, editBit: FB.ALARMAS_TAB_CREAR_EDIT },
    { key: "administrarAlarmas", label: "Administrar", viewBit: FB.ALARMAS_TAB_ADMIN_VIEW, editBit: FB.ALARMAS_TAB_ADMIN_EDIT },
    { key: "historialAlarmas", label: "Historial y reconocer", viewBit: FB.ALARMAS_TAB_HISTORIAL_VIEW, editBit: FB.ALARMAS_TAB_HISTORIAL_EDIT },
  ],
  "Usuarios Temporales": [
    { key: "crearUsuario", label: "Crear Usuario", viewBit: FB.USUARIOS_TEMP_TAB_CREAR_VIEW, editBit: FB.USUARIOS_TEMP_TAB_CREAR_EDIT },
    { key: "administrar", label: "Administrar Usuarios", viewBit: FB.USUARIOS_TEMP_TAB_EDITAR_VIEW, editBit: FB.USUARIOS_TEMP_TAB_EDITAR_EDIT },
  ],
  Reportería: [
    { key: "lecturas", label: "Lecturas de sensores", viewBit: FB.REPORTERIA_TAB_LECTURAS_VIEW, editBit: FB.REPORTERIA_TAB_LECTURAS_EDIT },
    { key: "operacion", label: "Operación", viewBit: FB.REPORTERIA_TAB_OPERACION_VIEW, editBit: FB.REPORTERIA_TAB_OPERACION_EDIT },
    { key: "auditoria", label: "Auditoría de mi cuenta", viewBit: FB.REPORTERIA_TAB_AUDITORIA_VIEW, editBit: FB.REPORTERIA_TAB_AUDITORIA_EDIT },
    { key: "alarmas", label: "Alarmas", viewBit: FB.REPORTERIA_TAB_ALARMAS_VIEW, editBit: FB.REPORTERIA_TAB_ALARMAS_EDIT },
    { key: "automatizacion", label: "Automatización", viewBit: FB.REPORTERIA_TAB_AUTOMATIZACION_VIEW, editBit: FB.REPORTERIA_TAB_AUTOMATIZACION_EDIT },
    { key: "listaEstanques", label: "Estanques", viewBit: FB.ESTANQUES_TAB_LISTA_VIEW, editBit: FB.ESTANQUES_TAB_LISTA_EDIT },
    { key: "listaDispositivos", label: "Dispositivos", viewBit: FB.DISPOSITIVOS_TAB_LISTA_VIEW, editBit: FB.DISPOSITIVOS_TAB_LISTA_EDIT },
    { key: "listaSensores", label: "Sensores", viewBit: FB.SENSORES_TAB_LISTA_VIEW, editBit: FB.SENSORES_TAB_LISTA_EDIT },
    { key: "listaEfectores", label: "Efectores", viewBit: FB.EFECTORES_TAB_LISTA_VIEW, editBit: FB.EFECTORES_TAB_LISTA_EDIT },
    { key: "listaUsuariosTemp", label: "Usuarios Temporales", viewBit: FB.USUARIOS_TEMP_TAB_LISTA_VIEW, editBit: FB.USUARIOS_TEMP_TAB_LISTA_EDIT },
  ],
  Configuraciones: [
    { key: "perfil", label: "Perfil", viewBit: FB.CONFIGURACIONES_TAB_PERFIL_VIEW, editBit: FB.CONFIGURACIONES_TAB_PERFIL_EDIT },
    { key: "parametros", label: "Parámetros", viewBit: FB.CONFIGURACIONES_TAB_PARAMETROS_VIEW, editBit: FB.CONFIGURACIONES_TAB_PARAMETROS_EDIT },
    { key: "parametrosLista", label: "Parámetros - Lista", viewBit: FB.CONFIGURACIONES_PARAMETROS_LISTA_VIEW, editBit: FB.CONFIGURACIONES_PARAMETROS_LISTA_EDIT },
    { key: "parametroEditar", label: "Parámetro - Editar", viewBit: FB.CONFIGURACIONES_PARAMETRO_EDITAR_VIEW, editBit: FB.CONFIGURACIONES_PARAMETRO_EDITAR_EDIT },
    { key: "firmware", label: "Firmware", viewBit: FB.CONFIGURACIONES_TAB_FIRMWARE_VIEW, editBit: FB.CONFIGURACIONES_TAB_FIRMWARE_EDIT },
    { key: "fwFlash", label: "Firmware - Flashear", viewBit: FB.FIRMWARE_TAB_FLASH_VIEW, editBit: FB.FIRMWARE_TAB_FLASH_EDIT },
    { key: "fwConfig", label: "Firmware - Configurar", viewBit: FB.FIRMWARE_TAB_CONFIG_VIEW, editBit: FB.FIRMWARE_TAB_CONFIG_EDIT },
    { key: "fwOta", label: "Firmware - OTA", viewBit: FB.FIRMWARE_TAB_OTA_VIEW, editBit: FB.FIRMWARE_TAB_OTA_EDIT },
    { key: "respaldo", label: "Respaldo", viewBit: FB.CONFIGURACIONES_TAB_RESPALDO_VIEW, editBit: FB.CONFIGURACIONES_TAB_RESPALDO_EDIT },

  ],
};

// Solo garantizamos el bit 1 (INICIO_VIEW).
const ensureCreateTemporalMin = (bitset) => forceBit1(bitset);

const CrearUsuarioTemporal = ({ onUsuarioCreado }) => {
  // Datos principales
  const [nombre, setNombre] = useState("");
  const [correo, setCorreo] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [confirmarContrasena, setConfirmarContrasena] = useState("");
  const [mostrarContrasena, setMostrarContrasena] = useState(false);
  const [expiracion, setExpiracion] = useState(24);
  const [unidadExpiracion, setUnidadExpiracion] = useState("horas");
  const [rolId, setRolId] = useState("");

  // Endpoint binario (90 bits) con bit 1 siempre ON
  const [bitset, setBitset] = useState(emptyBitset());

  // Verificación de correo
  const [correoVerificado, setCorreoVerificado] = useState(false);
  const [codigoEnviado, setCodigoEnviado] = useState("");
  const [codigoIngresado, setCodigoIngresado] = useState("");
  const [verificandoCorreo, setVerificandoCorreo] = useState(false);

  // Anti-doble submit
  const [creando, setCreando] = useState(false);
  const { bitset: permisosCreador } = usePermisos();
  const [plantilla, setPlantilla] = useState("Personalizado");
  const [estanques, setEstanques] = useState([]);
  const [estanquesIds, setEstanquesIds] = useState([]);
  useEffect(() => { api.get('/Estanques/mis-estanques').then(r => setEstanques((r.data || []).filter(e => {
      let id; try {id=JSON.parse(atob(localStorage.getItem('token').split('.')[1].replace(/-/g,'+').replace(/_/g,'/'))).id;} catch {return false;}
      return e.estaActivo && (isSuperAdmin() || String(e.usuarioId)===String(id));
    }))).catch(() => setEstanques([])); }, []);

  // Toggle ventana (grupo)
  const toggleVentana = (windowName) => {
    setPlantilla("Personalizado");
    const features = WINDOW_TO_FEATURES[windowName] || [];
    const currentOn = features.some((f) => getBit(bitset, FB[f]));
    let next = bitset;

    for (const f of features) next = setBit(next, FB[f], !currentOn);

    // Si apagamos una ventana, apagamos sus pestañas
    if (currentOn === true && TABS_BITS[windowName]) {
      for (const t of TABS_BITS[windowName]) {
        next = setBit(next, t.viewBit, false);
        if (t.editBit) next = setBit(next, t.editBit, false);
      }
    }

    setBitset(limitarPermisosAlCreador(forceBit1(next), permisosCreador, isSuperAdmin()));
  };

  // Toggle feature (VIEW/EDIT) del grupo ventana
  const toggleFeature = (featureKey) => {
    setPlantilla("Personalizado");
    const pos = FB[featureKey];
    const isOn = getBit(bitset, pos);
    let next = setBit(bitset, pos, !isOn);

    // Si apagan USUARIOS_TEMP_VIEW, apaga sus tabs de vista
    if (featureKey === "USUARIOS_TEMP_VIEW" && isOn) {
      for (const k of ["USUARIOS_TEMP_TAB_CREAR_VIEW", "USUARIOS_TEMP_TAB_EDITAR_VIEW"]) {
        if (FB[k]) next = setBit(next, FB[k], false);
      }
    }

    setBitset(limitarPermisosAlCreador(forceBit1(next), permisosCreador, isSuperAdmin()));
  };

  // Toggle de pestaña
  const toggleTab = (e, bit) => {
    setPlantilla("Personalizado");
    e.stopPropagation();
    setBitset((prev) => limitarPermisosAlCreador(forceBit1(setBit(prev, bit, !getBit(prev, bit))), permisosCreador, isSuperAdmin()));
  };

  // ====== Acciones ======
  const handleVerificarCorreo = async () => {
    if (!correo) {
      alert("Ingrese un correo electrónico antes de verificar.");
    } else {
      try {
        setVerificandoCorreo(true);
        await usuarioService.verificarCorreoTemporal(correo);
        setCodigoEnviado("ENVIADO");
        alert("Se ha enviado un código al correo.");
      } catch (err) {
        console.error(err);
        alert(err.response?.data || "Error al verificar el correo.");
      } finally {
        setVerificandoCorreo(false);
      }
    }
  };

  const handleValidarCodigo = async () => {
    try {
      await usuarioService.validarCorreoTemporal(correo, codigoIngresado);
      setCorreoVerificado(true);
      alert("Correo verificado correctamente.");
    } catch (err) {
      setCorreoVerificado(false);
      alert(err.response?.data || "El código ingresado no es correcto o expiró.");
    }
  };

  const handleCrearUsuario = async () => {
    if (creando) return;

    if (contrasena !== confirmarContrasena) {
      alert("Las contraseñas no coinciden");
      return;
    }
    if (!nombre || !correo || !contrasena || !rolId) {
      alert("Todos los campos son obligatorios");
      return;
    }
    if (!correoVerificado) {
      alert("Debe verificar el correo antes de continuar.");
      return;
    }

    let expiracionHoras;
    try { expiracionHoras = duracionEnHoras(expiracion, unidadExpiracion); }
    catch (error) { alert(error.message); return; }

    const payload = {
      nombre,
      email: correo,
      contrasena,
      expiracionHoras,
      rolId: parseInt(rolId, 10),
      endpoint: clampToLength(bitset, BITSET_LEN),
      estanquesIds,
    };

    try {
      setCreando(true);
      await usuarioService.crearTemporal(payload);
      alert("Usuario temporal creado con éxito");

      // Limpiar formulario
      setNombre("");
      setCorreo("");
      setContrasena("");
      setConfirmarContrasena("");
      setCorreoVerificado(false);
      setCodigoEnviado("");
      setCodigoIngresado("");
      setRolId("");
      setExpiracion(24);
      setUnidadExpiracion("horas");
      setBitset(emptyBitset());
      setEstanquesIds([]);
      setPlantilla("Personalizado");

      onUsuarioCreado && onUsuarioCreado();
    } catch (error) {
      console.error("Error al crear usuario:", error);
      const status = error.response?.status;
      const data = error.response?.data;
      const mensaje =
        typeof data === "string" ? data : data?.mensaje || data?.error || JSON.stringify(data);
      alert(`Error ${status || ""}: ${mensaje || error.message}`);
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white py-8 px-4">
      <form
        className="max-w-5xl mx-auto space-y-6"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          handleCrearUsuario();
        }}
      >
        <h3 className="text-2xl font-bold text-emerald-400">Crear Usuario Temporal</h3>
        <label className="block text-sm font-semibold">Plantilla de permisos
          <select className="w-full border border-gray-600/60 rounded-lg px-4 py-2 bg-neutral-900" value={plantilla} onChange={e => {
            const value=e.target.value; setPlantilla(value);
            if(value !== 'Personalizado') setBitset(permissionTemplate(value, permisosCreador, isSuperAdmin()));
          }}>{['Administrador','Consulta','Operador','Técnico','Personalizado'].map(p => <option key={p}>{p}</option>)}</select>
          <span className="mt-1 block text-xs font-normal text-neutral-400">La plantilla propone permisos. El rol se elige por separado y no cambia al seleccionar una plantilla.</span>
        </label>
        <div><p>Estanques asignados (incluye sus dispositivos, sensores y efectores):</p>{estanques.map(e => <label className="block" key={e.id}><input type="checkbox" checked={estanquesIds.includes(e.id)} onChange={() => setEstanquesIds(ids => ids.includes(e.id) ? ids.filter(id => id !== e.id) : [...ids,e.id])}/> {e.nombre}</label>)}<p className="text-sm text-neutral-400">Acceso a {estanquesIds.length} estanque(s), durante {expiracion} {UNIDAD_EXPIRACION_LABEL[unidadExpiracion]}. {getBit(bitset, FB.EFECTORES_EDIT) ? 'Puede enviar órdenes a efectores.' : 'No puede enviar órdenes a efectores.'} {getBit(bitset, FB.CONFIGURACIONES_EDIT) ? 'Puede modificar las configuraciones autorizadas.' : 'No puede modificar configuración.'} Sin estanques asignados no tendrá acceso a sus equipos.</p></div>

        {/* Datos */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold">Nombre de usuario</label>
            <input
              type="text"
              autoComplete="off"
              name="nombreTemporal"
              className="w-full border border-gray-600/60 rounded-lg px-4 py-2 bg-neutral-900 placeholder:text-neutral-400"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              disabled={creando}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold">Correo electrónico</label>
            <input
              type="email"
              autoComplete="off"
              name="emailTemporal"
              className="w-full border border-gray-600/60 rounded-lg px-4 py-2 bg-neutral-900 placeholder:text-neutral-400"
              value={correo}
              onChange={(e) => {
                setCorreo(e.target.value);
                setCorreoVerificado(false);
              }}
              disabled={creando}
            />
          </div>

          {!correoVerificado && (
            <div className="sm:col-span-2 flex flex-wrap gap-3 items-center">
              <button
                type="button"
                onClick={handleVerificarCorreo}
                disabled={verificandoCorreo || creando}
                className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 px-4 py-2 rounded-lg"
              >
                {verificandoCorreo ? "Enviando..." : "Verificar correo"}
              </button>
              {codigoEnviado && (
                <>
                  <input
                    type="text"
                    autoComplete="one-time-code"
                    name="pinCorreoTemporal"
                    placeholder="Código recibido"
                    value={codigoIngresado}
                    onChange={(e) => setCodigoIngresado(e.target.value)}
                    className="border border-gray-600/60 px-4 py-2 rounded-lg bg-neutral-900"
                    disabled={creando}
                  />
                  <button
                    type="button"
                    onClick={handleValidarCodigo}
                    className="bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg disabled:opacity-60"
                    disabled={creando}
                  >
                    Validar código
                  </button>
                </>
              )}
            </div>
          )}

          <div className="relative">
            <label className="block text-sm font-semibold">Contraseña</label>
            <p className="mb-1 text-xs font-normal text-neutral-400">Se usa junto al correo para iniciar sesión en esta cuenta temporal. Cada cuenta temporal tiene su propia clave; esta no cambia la contraseña de la cuenta principal.</p>
            <input
              type={mostrarContrasena ? "text" : "password"}
              autoComplete="new-password"
              name="passwordTemporal"
              className="w-full border border-gray-600/60 rounded-lg px-4 py-2 pr-10 bg-neutral-900 placeholder:text-neutral-400"
              value={contrasena}
              onChange={(e) => setContrasena(e.target.value)}
              disabled={creando}
            />
            <button
              type="button"
              className="absolute top-8 right-3"
              onClick={() => setMostrarContrasena(!mostrarContrasena)}
              aria-label="mostrar/ocultar"
              disabled={creando}
            >
              {mostrarContrasena ? "🙈" : "👁️"}
            </button>
          </div>

          <div>
            <label className="block text-sm font-semibold">Confirmar contraseña</label>
            <input
              type={mostrarContrasena ? "text" : "password"}
              autoComplete="new-password"
              name="confirmTemporal"
              className="w-full border border-gray-600/60 rounded-lg px-4 py-2 bg-neutral-900 placeholder:text-neutral-400"
              value={confirmarContrasena}
              onChange={(e) => setConfirmarContrasena(e.target.value)}
              disabled={creando}
            />
          </div>

          <div>
            <p className="block text-sm font-semibold">Expiración</p>
            <DuracionTemporal cantidad={expiracion} unidad={unidadExpiracion}
              onCantidadChange={setExpiracion} onUnidadChange={setUnidadExpiracion} disabled={creando} />
          </div>

          <div>
            <label className="block text-sm font-semibold">Seleccionar rol</label>
            <select
              autoComplete="off"
              name="rolTemporal"
              className="w-full border border-gray-600/60 rounded-lg px-4 py-2 bg-neutral-900"
              value={rolId}
              onChange={(e) => {
                const nextRol = e.target.value;
                setRolId(nextRol); setPlantilla("Personalizado");
                setBitset(limitarPermisosAlCreador(bitsetPredeterminadoPorRol(nextRol), permisosCreador, isSuperAdmin()));
              }}
              disabled={creando}
            >
              <option value="">Seleccionar...</option>
              <option value="1">Administrador</option>
              <option value="2">Operador</option>
              <option value="3">Consultor</option>
            </select>
            <p className="mt-1 text-xs text-neutral-400">
              El rol clasifica la cuenta. Al elegirlo se marcan permisos recomendados; puedes personalizarlos abajo. Los permisos no superarán los que tú puedes conceder.
            </p>
          </div>
        </div>

<PermisosTableros />
        {/* Permisos por ventana */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-lg font-semibold text-emerald-400">Permisos por ventana</h4>
            <div className="flex gap-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded bg-neutral-800 border border-neutral-700 text-sm"
                onClick={() => setBitset(emptyBitset())}
                disabled={creando}
              >
                Limpiar todo
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded bg-neutral-800 border border-neutral-700 text-sm"
                onClick={() => {
                  let next = emptyBitset();
                  Object.values(FB).forEach((pos) => {
                    if (typeof pos === "number") next = setBit(next, pos, true);
                  });
                  setBitset(limitarPermisosAlCreador(forceBit1(next), permisosCreador, isSuperAdmin()));
                }}
                disabled={creando}
              >
                Activar todo
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {WINDOWS_ORDER.map((w) => {
              const active = isWindowActive(w, bitset);
              const tabDefs = TABS_BITS[w] || [];
              const isInicio = ["Inicio", "Acerca de", "Contáctanos"].includes(w);

              return (
                <div
                  key={w}
                  className={`rounded-lg border px-3 py-3 transition-colors ${
                    active
                      ? "bg-emerald-400/10 border-emerald-500/40"
                      : "bg-neutral-900/60 border-neutral-700"
                  } ${isInicio ? "cursor-default" : "cursor-pointer"}`}
                  onClick={() => !isInicio && !creando && toggleVentana(w)}
                  title={
                    isInicio
                      ? "Esta ventana siempre está habilitada"
                      : "Click para activar/desactivar (Ver/Editar)"
                  }
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{w}</span>
                    <span className={`text-xs ${active ? "text-emerald-300" : "text-neutral-400"}`}>
                      {["Inicio", "Acerca de", "Contáctanos"].includes(w) ? "Siempre activo" : active ? "ON" : "OFF"}
                    </span>
                  </div>

                  {/* Botones Ver/Editar de la ventana */}
                  <div className="mt-1 flex flex-wrap gap-2">
                    {(WINDOW_TO_FEATURES[w] || []).map((fk) => {
                      const on = getBit(bitset, FB[fk]);
                      const isInicioView = ["INICIO_VIEW", "ACERCA_VIEW", "CONTACTO_VIEW"].includes(fk); // siempre activo
                      const label =
                        fk.endsWith("_VIEW")
                          ? "Ventana · Abrir"
                          : fk.endsWith("_EDIT")
                          ? "Ventana · Modificar"
                          : fk;
                      return (
                        <button
                          key={fk}
                          type="button"
                          className={`text-xs px-2 py-1 rounded border ${
                            on
                              ? "bg-emerald-600/80 border-emerald-500"
                              : "bg-neutral-800 border-neutral-700"
                          } ${isInicioView ? "opacity-60 cursor-not-allowed" : ""}`}
                          onClick={(e) => {
                            if (isInicioView || creando) return;
                            e.stopPropagation();
                            toggleFeature(fk);
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Pestañas de la ventana */}
                  {tabDefs.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {tabDefs.map((t) => {
                        const viewOn = getBit(bitset, t.viewBit);
                        const editOn = t.editBit ? getBit(bitset, t.editBit) : false;
                        return (
                          <div key={t.key} className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              className={`text-[11px] px-2 py-1 rounded border ${
                                viewOn
                                  ? "bg-emerald-600/80 border-emerald-500"
                                  : "bg-neutral-800 border-neutral-700"
                              }`}
                              onClick={(e) => {
                                if (creando) return;
                                toggleTab(e, t.viewBit);
                              }}
                              title={`${t.label}: abrir sección`}
                            >
                              {t.label} · Abrir
                            </button>
                            {t.editBit && (
                              <button
                                type="button"
                                className={`text-[11px] px-2 py-1 rounded border ${
                                  editOn
                                    ? "bg-emerald-600/80 border-emerald-500"
                                    : "bg-neutral-800 border-neutral-700"
                                }`}
                                onClick={(e) => {
                                  if (creando) return;
                                  toggleTab(e, t.editBit);
                                }}
                                title={`${t.label}: modificar sección`}
                              >
                                {t.label} · {w === "Reportería" ? "Exportar" : "Modificar"}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Diagnóstico de permisos exclusivo del superadministrador. */}
          {isSuperAdmin() && <div className="mt-4 text-sm text-neutral-300">
            <span className="font-semibold">Bitset ({BITSET_LEN}):</span>{" "}
            <code className="bg-neutral-900 px-2 py-1 rounded border border-neutral-800">
              {bitset}
            </code>
          </div>}
        </section>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="px-3 py-1.5 rounded bg-neutral-800 border border-neutral-700 text-sm"
            onClick={() => setBitset((prev) => ensureCreateTemporalMin(prev))}
            disabled={creando}
          >
            Mínimos para crear temporal
          </button>
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={!correoVerificado || creando}
            className={`flex-1 ${
              !correoVerificado || creando
                ? "bg-neutral-700 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-700"
            } text-white font-semibold py-2 px-4 rounded-lg shadow-md transition`}
          >
            {creando ? "Creando..." : "Crear Usuario"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CrearUsuarioTemporal;
